import type { ChannelType, Notify } from '../api/types'
import { mentionedUsernames } from '../lib/text/tokenize'
import type { SoundName } from './clips'
import type { SoundSettings } from './settings'

/** Minimum gap between sounds so a burst of messages makes one ping. Mentions may cut in sooner. */
export const THROTTLE_MS = 1500
export const MENTION_THROTTLE_MS = 400

export interface MessageSoundInput {
  settings: SoundSettings
  /** document.visibilityState === 'visible'. Hidden pages stay silent: the push notification makes the sound. */
  visible: boolean
  now: number
  lastPlayedAt: number
  me: { id: string; username: string }
  /** `forwarded`: a forwarded copy, whose @mentions notify nobody (contract: "Forwarding"). */
  message: { authorId: string; body: string; deleted: boolean; forwarded?: boolean }
  channel: { id: string; type: ChannelType; notify: Notify; mutedUntil: string | null; isMember: boolean } | undefined
}

/** Which sound a live incoming message deserves, if any. */
export function soundForMessage(input: MessageSoundInput): SoundName | null {
  const { settings, message, channel, me, now } = input
  if (!settings.enabled || !input.visible) return null
  if (message.authorId === me.id || message.deleted) return null
  if (!channel || !channel.isMember) return null
  if (channel.mutedUntil && Date.parse(channel.mutedUntil) > now) return null
  if (channel.notify === 'none') return null

  const mentioned = !message.forwarded && mentionedUsernames(message.body).some((u) => u.toLowerCase() === me.username.toLowerCase())
  const candidates: SoundName[] = []
  if (mentioned) candidates.push('mention')
  if (channel.type !== 'channel') candidates.push('direct')
  else if (channel.notify === 'all') candidates.push('message')

  const sound = candidates.find((c) => settings.events[c]) ?? null
  if (!sound) return null
  const gap = now - input.lastPlayedAt
  if (gap < (sound === 'mention' ? MENTION_THROTTLE_MS : THROTTLE_MS)) return null
  return sound
}

export interface SimpleSoundInput {
  settings: SoundSettings
  visible: boolean
  now: number
  lastPlayedAt: number
}

export function soundForDeviceRequest(input: SimpleSoundInput & { isSysadmin: boolean }): SoundName | null {
  if (!input.isSysadmin || !input.settings.enabled || !input.visible || !input.settings.events.deviceRequest) return null
  return input.now - input.lastPlayedAt < THROTTLE_MS ? null : 'deviceRequest'
}

export function soundForSent(input: SimpleSoundInput): SoundName | null {
  if (!input.settings.enabled || !input.visible || !input.settings.events.sent) return null
  return input.now - input.lastPlayedAt < 250 ? null : 'sent'
}

/** Android vibration pattern for attention-worthy sounds; iOS ignores navigator.vibrate. */
export function vibrationFor(sound: SoundName, settings: SoundSettings): number[] | null {
  if (!settings.vibrate) return null
  if (sound === 'mention') return [60, 60, 60]
  if (sound === 'direct') return [80]
  return null
}
